/* eslint-disable consistent-return */
import { EventEmitter, SimpleChanges } from '@angular/core';
import angular, { ICompileService, IScope } from 'angular';

import { ModalOverlayService } from '../modal-overlay/modal-overlay.service';
import SelectOption from './select-option/select-option.component';
import './select.styles.scss';

export const SelectComponent = {
  selector: 'abSelect',
  restrict: 'E',
  bindings: {
    options: '<', // Values, which will be inserted into the input field
    customModel: '=',
    customModelChange: '&',
    multiSelectDelimiter: '@',
    autocomplete: '@',
    placeholder: '@?',
    quote: '@', // If set the results will be surrounded by quotes
    changeFunction: '&', // is called, when new value is selected
    required: '=',
    disabled: '=',
    classes: '@',
    showDescription: '@?',
    sort: '@',
    /** If return type should be array or string */
    customArrayChange: '&?',
  },
  template: /* html */`
    <div class="ab-select input-wrap select">
      <input ng-model="$ctrl.customModel" type="text" ng-focus="$ctrl.showOptions($event)" ng-click="$ctrl.showOptions($event)" placeholder="{{ $ctrl.placeholderText.text }}" ng-required="$ctrl.required" ng-disabled="$ctrl.disabled"/>
      <label>{{ $ctrl.placeholderText.text }}</label>

      <span class="input-description" ng-if="($ctrl.optionsHaveDescription || $ctrl.showDescription) && $ctrl.inputDescription !== ''" ng-bind-html="$ctrl.inputDescription"></span>
      <div class="btn-wrap" ng-hide="$ctrl.disabled">
        <button type="button" class="btn-lvl3 remove-button" ng-click="$ctrl.removeValue()" ng-show="$ctrl.customModel">
          <svg class="ab-icon"><use xlink:href="#btn-delete"></use></svg> 
        </button>
      </div>
    </div>
  `,
  controller: class SelectController {
    private options!: { key: string };

    private translations?: { key: string };

    private customModel!: string | undefined;

    private customModelChange!: (event: { $event: string | undefined }) => void;

    private multiSelectDelimiter?: string;

    private autocomplete?: boolean;

    private placeholder?: string;

    private quote?: boolean;

    private changeFunction?: (args: any) => void;

    private required?: boolean;

    private disabled?: boolean;

    private showDescription?: boolean;

    private sort?: boolean;

    private customArrayChange?: (event: undefined | string[]) => void;

    private selectables: Array<{
      value: string,
      description: string,
      selected: boolean,
    }> = [];

    private multiSelect = false;

    private inputDescription = '';

    private optionsHaveDescription = false;

    private modalOverlayService: ModalOverlayService;

    private $compile: ICompileService;

    private $scope: IScope;

    private placeholderText: {
      text: string,
    } = {
      text: '',
    };

    constructor(
      modalOverlayService: ModalOverlayService,
      $compile: ICompileService,
      $scope: IScope,
    ) {
      'ngInject';

      this.modalOverlayService = modalOverlayService;
      this.$compile = $compile;
      this.$scope = $scope;
    }

    $onChanges(event: SimpleChanges): void {
      try {
        if (event.options) return this.init();

        if (!this.customModel) {
          return this.resetOptions();
        }
        this.handleChanges(event.customModel?.currentValue);
      } catch (e) {
        console.info(e);
      }
    }

    public $onInit = (): void => {
      this.init();
    };

    private init = (): void => {
      this.selectables = [];
      this.multiSelect = this.multiSelectDelimiter !== undefined;

      this.placeholderText.text = this.placeholder || 'Hello';

      let previouslySelected: string[] | string = [];

      if (this.customModel !== undefined) {
        previouslySelected = this.customModel;
        if (this.multiSelect && !Array.isArray(this.customModel)) {
          previouslySelected = previouslySelected.split(this.multiSelectDelimiter as string);
        }
      }

      if (Array.isArray(this.options)) {
        for (const value of this.options) {
          this.selectables.push({
            value,
            description: value,
            selected: previouslySelected.indexOf(value) >= 0,
          });
        }

        if (this.sort) {
          this.selectables = this
            .selectables.sort((a, b) => a.description.localeCompare(b.description));
        }
      } else {
        for (const [value, description] of (Object as any).entries(this.options)) {
          this.selectables.push({
            value,
            description,
            selected: previouslySelected.indexOf(value) >= 0,
          });
        }

        if (this.sort) {
          this.selectables = this
            .selectables.sort((a, b) => a.description.localeCompare(b.description));
        }

        if (!this.multiSelect) {
          this.optionsHaveDescription = true;
          if (this.customModel !== undefined) this.selectOption(this.customModel);
        }
      }

      this.showInputDescription();
    };

    /**
     * After click on input field.
     * Prevent any custom input and show the options with help of the modal service
     */
    public showOptions = (event: Event): void => {
      if (event.target === null || this.disabled) return;
      (event.target as any).blur();

      const optionsHtml = /* html */`
        <ul class="ab-select-options">
          <ab-select-option ng-repeat="option in $ctrl.selectables track by $index" ng-click="$ctrl.selectOption(option.value)"></ab-select-option>
        </ul>
      `;

      const inputPosition = (event.target as any).getBoundingClientRect();
      const compiledHtml = this.$compile(optionsHtml)(this.$scope);

      setTimeout(() => {
        const optionsHeight = angular.element(compiledHtml)[0].clientHeight;

        // TODO: objectHeight is always 0 here, because the object will be only rendered after it is shown
        // todo: therefore the elements height is only then known

        this.modalOverlayService.show((compiledHtml as any), inputPosition, optionsHeight);
      }, 50);
    };

    private showInputDescription = () => {
      if (!this.showDescription && !this.optionsHaveDescription) return;
      const selectedOptions = this.selectables.filter((s) => s.selected);
      if (!selectedOptions.length) {
        this.inputDescription = '';
        return;
      }

      if (this.multiSelect) {
        if (selectedOptions.length <= 2) {
          this.inputDescription = `${selectedOptions.map((o) => o.description).join(', ')}`;
        } else {
          this.inputDescription = selectedOptions.length === 1 ? 'Eine Option gewählt' : `${selectedOptions.length} Optionen gewählt`;
        }
      } else {
        this.inputDescription = selectedOptions[0].description;
      }
    };

    private updateModel = (value: string | undefined): void => {
      this.customModel = value;

      if (this.customArrayChange) {
        if (!value) {
          this.customArrayChange([]);
        } else if (this.multiSelect) {
          this.customArrayChange(value.split(this.multiSelectDelimiter || ','));
        } else {
          this.customArrayChange([value]);
        }
      }

      this.showInputDescription();
    };

    private updateModelMulti = (): void => {
      const selectedOptions: number[] | string[] = this.selectables
        .filter((s) => s.selected)
        .map((s) => (this.quote ? `"${s.value}"` : s.value));

      this.showInputDescription();
      if (!selectedOptions.length) {
        this.updateModel(undefined);
      } else {
        this.updateModel(selectedOptions.join(this.multiSelectDelimiter));
      }

      if (this.changeFunction) {
        this.changeFunction(null);
      }
    };

    private handleChanges = (customModel?: string): void => {
      if (customModel && this.changeFunction) {
        this.changeFunction(null);
      }

      if (!customModel) return;
      if (this.multiSelect) {
        const values = customModel.split(this.multiSelectDelimiter || ',');

        this.selectables.forEach((s) => {
          s.selected = values.includes(s.value);
        });
      } else {
        this.selectables.forEach((s) => {
          s.selected = customModel === s.value;
        });
      }

      this.showInputDescription();
    };

    /**
     * User selects an option from the dropdown.
     */
    private selectOption = (optionValue?: string): void => {
      if (!this.multiSelect && optionValue) {
        this.resetOptions();
        this.hideOptions();

        if (this.changeFunction) {
          this.changeFunction(null);
        }
      }

      const selectedOption = this.selectables.find((s) => s.value === optionValue);
      this.showInputDescription();
      if (!selectedOption) return;

      selectedOption.selected = !selectedOption.selected;

      if (this.multiSelect) return this.updateModelMulti();
      return this.updateModel(selectedOption.value);
    };

    private resetOptions = () => {
      for (const o of this.selectables) {
        o.selected = false;
      }
      this.showInputDescription();

      // if (this.changeFunction) {
      //   this.changeFunction(null);
      // }
    };

    private hideOptions = () => {
      this.modalOverlayService.hide();
    };

    public removeValue = (): void => {
      this.updateModel(undefined);
      this.resetOptions();

      if (this.changeFunction) {
        this.changeFunction(null);
      }
    };
  },
};

export default angular.module('components.abSelect', [SelectOption])
  .component(SelectComponent.selector, SelectComponent)
  .name;
